

import React from 'react';

// Support — управление сотрудниками техподдержки.
//
// Ожидаемый API (на бэке):
//   GET    /api/support/users                 -> { ok:true, users:[{id, username, name, added_ts}] }
//   POST   /api/support/users                 body { username:"support" } -> { ok:true }
//   DELETE /api/support/users/:username       -> { ok:true }
//
// Авторизация Mini App: заголовок x-telegram-init-data.

function getInitData() {
  try {
    return window.Telegram?.WebApp?.initData || '';
  } catch (_) {
    return '';
  }
}

async function fetchJson(path, { method = 'GET', body } = {}) {
  const res = await fetch(path, {
    method,
    headers: {
      'content-type': 'application/json',
      'x-telegram-init-data': getInitData()
    },
    body: body ? JSON.stringify(body) : undefined
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.ok === false) {
    const msg = data?.error || data?.message || `HTTP ${res.status}`;
    const err = new Error(msg);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

function normalizeUsername(raw) {
  let u = String(raw || '').trim();
  if (!u) return '';
  if (u.startsWith('@')) u = u.slice(1);
  // Telegram username: A-Z a-z 0-9 _ (5..32)
  // Здесь не режем жёстко, но чистим очевидное.
  u = u.replace(/[^a-zA-Z0-9_]/g, '');
  return u;
}

function fmtTs(ts) {
  if (!ts) return '—';
  try {
    return new Date(ts * 1000).toLocaleString();
  } catch (_) {
    return String(ts);
  }
}

export default function Support() {
  const [username, setUsername] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');
  const [users, setUsers] = React.useState([]);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchJson('/api/support/users');
      setUsers(Array.isArray(data?.users) ? data.users : []);
    } catch (e) {
      setUsers([]);
      setError(e?.message || 'Ошибка загрузки сотрудников');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  const add = async () => {
    const u = normalizeUsername(username);
    if (!u) return;

    setSaving(true);
    setError('');
    try {
      await fetchJson('/api/support/users', { method: 'POST', body: { username: u } });
      setUsername('');
      await load();
    } catch (e) {
      setError(e?.message || 'Ошибка добавления');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (u) => {
    const uu = normalizeUsername(u);
    if (!uu) return;

    // Простое подтверждение без модалок
    // eslint-disable-next-line no-alert
    const ok = window.confirm(`Удалить сотрудника @${uu}?`);
    if (!ok) return;

    setSaving(true);
    setError('');
    try {
      await fetchJson(`/api/support/users/${encodeURIComponent(uu)}`, { method: 'DELETE' });
      await load();
    } catch (e) {
      setError(e?.message || 'Ошибка удаления');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.wrap}>
      <div style={styles.header}>
        <h2 style={styles.title}>👨‍🔧 Техподдержка</h2>
        <div style={styles.muted}>Добавляйте сотрудников по Telegram username (без добавления бота в клиентские чаты).</div>
      </div>

      {error ? <div style={styles.error}>⛔ {error}</div> : null}

      <div style={styles.card}>
        <div style={styles.cardTitle}>➕ Добавить сотрудника</div>
        <div style={styles.controls}>
          <input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="username или @username"
            style={styles.input}
            disabled={saving}
          />
          <button
            onClick={add}
            disabled={saving || !normalizeUsername(username)}
            style={{ ...styles.button, ...(saving || !normalizeUsername(username) ? styles.buttonDisabled : null) }}
          >
            {saving ? '⏳' : '✅'} Добавить
          </button>
        </div>
        <div style={styles.hint}>Пример: <span style={styles.mono}>@support_pb</span></div>
      </div>

      <div style={styles.card}>
        <div style={styles.cardTop}>
          <div style={styles.cardTitle}>📋 Список сотрудников</div>
          <button onClick={load} disabled={loading || saving} style={styles.secondaryBtn}>
            🔄 Обновить
          </button>
        </div>

        <div style={styles.meta}>Всего: {users.length}</div>

        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th>Username</th>
                <th>Имя</th>
                <th>Добавлен</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.username || u.id || Math.random()}>
                  <td style={styles.mono}>@{u.username || '—'}</td>
                  <td>{u.name || '—'}</td>
                  <td>{fmtTs(u.added_ts)}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button
                      onClick={() => remove(u.username)}
                      disabled={saving}
                      style={{ ...styles.dangerBtn, ...(saving ? styles.buttonDisabled : null) }}
                    >
                      🗑 Удалить
                    </button>
                  </td>
                </tr>
              ))}
              {!users.length && !loading ? (
                <tr>
                  <td colSpan={4} style={styles.empty}>Сотрудников пока нет</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <div style={styles.note}>
          <div style={styles.noteTitle}>Как бот использует список поддержки</div>
          <ul style={styles.ul}>
            <li>Сообщения от этих пользователей считаются ответами техподдержки для SLA.</li>
            <li>Если username сменится — нужно обновить запись (удалить/добавить заново).</li>
          </ul>
        </div>
      </div>

      <div style={styles.muted}>
        Источник: <span style={styles.mono}>/api/support/users</span>
      </div>
    </div>
  );
}

const styles = {
  wrap: { padding: 12 },
  header: { marginBottom: 10 },
  title: { margin: '0 0 6px 0' },
  muted: { fontSize: 12, opacity: 0.7 },

  error: {
    background: '#fee2e2',
    color: '#7f1d1d',
    padding: 10,
    borderRadius: 10,
    marginBottom: 10
  },

  card: {
    border: '1px solid rgba(0,0,0,0.12)',
    borderRadius: 12,
    padding: 12,
    background: '#fff',
    marginBottom: 12
  },
  cardTitle: { fontWeight: 800, marginBottom: 8 },
  cardTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },

  controls: { display: 'flex', gap: 8, alignItems: 'center' },
  input: {
    flex: 1,
    padding: '8px 10px',
    borderRadius: 10,
    border: '1px solid rgba(0,0,0,0.15)',
    outline: 'none'
  },
  hint: { marginTop: 8, fontSize: 12, opacity: 0.7 },
  meta: { marginTop: 8, fontSize: 12, opacity: 0.75 },

  button: {
    padding: '9px 12px',
    borderRadius: 10,
    border: '1px solid rgba(0,0,0,0.2)',
    cursor: 'pointer'
  },
  secondaryBtn: {
    padding: '8px 10px',
    borderRadius: 10,
    border: '1px solid rgba(0,0,0,0.2)',
    cursor: 'pointer'
  },
  dangerBtn: {
    padding: '8px 10px',
    borderRadius: 10,
    border: '1px solid rgba(220,38,38,0.35)',
    background: 'rgba(220,38,38,0.06)',
    cursor: 'pointer'
  },
  buttonDisabled: { opacity: 0.55, cursor: 'not-allowed' },

  tableWrap: { overflowX: 'auto', marginTop: 8 },
  table: { width: '100%', borderCollapse: 'collapse' },
  empty: { textAlign: 'center', padding: 16, opacity: 0.65 },

  note: {
    marginTop: 12,
    padding: 10,
    borderRadius: 10,
    background: 'rgba(0,0,0,0.03)',
    border: '1px solid rgba(0,0,0,0.08)'
  },
  noteTitle: { fontWeight: 800, marginBottom: 6, fontSize: 13 },
  ul: { margin: 0, paddingLeft: 18 },

  mono: { fontFamily: 'monospace' }
};